/**
 * AuthProvider - React context wrapping the Keycloak singleton.
 *
 * Why a context, not a hook directly: the keycloak init is async +
 * stateful; components need to know whether init has completed +
 * whether the user is authenticated. The context exposes
 * { status, login, logout } so consumers don't need to know about
 * the underlying singleton.
 *
 * Wraps the whole app at main.tsx (BELOW QueryClientProvider so
 * react-query doesn't fire requests before the token is wired in;
 * useMe + framework hooks all read setAuthTokenGetter at request
 * time so the order is "init keycloak first, then let react-query
 * make its first call").
 *
 * Status values:
 *   "initializing" - first render, init() in flight, render a small
 *                    full-page spinner
 *   "unauthenticated" - init done, no session, render <LoginGate>
 *                       that prompts to click login
 *   "authenticated"   - init done, valid token, render <App />
 *
 * The api client + react-query handle 401s independently - if a
 * token expires mid-session and refresh fails, the adapter's
 * onTokenExpired handler triggers logout + we drop back to
 * "unauthenticated" on the next mount.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { api } from "shared/api/client";
import {
  initKeycloak,
  login,
  logout,
  takePasswordChanged,
} from "shared/auth/keycloakAdapter";

type AuthStatus = "initializing" | "unauthenticated" | "authenticated" | "error";

interface AuthContextValue {
  status: AuthStatus;
  /** Populated when status === "error". null otherwise. */
  error: Error | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("initializing");
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    initKeycloak()
      .then((authenticated) => {
        if (cancelled) return;
        setStatus(authenticated ? "authenticated" : "unauthenticated");
        // Round 17 K19 -- a password changed in the identity provider's
        // step goes on the record, once. Best effort: the change has
        // already happened, and a failed report must not block sign-in.
        if (authenticated && takePasswordChanged()) {
          void api.post("/v1/me/password-changed", {}).catch(() => undefined);
        }
      })
      .catch((err) => {
        // Init can fail if VITE_KEYCLOAK_* is missing, Keycloak is
        // down, or the code exchange after redirect fails (CORS,
        // expired auth code, realm changed mid-flight). We previously
        // dropped to "unauthenticated" silently here, which produced
        // an opaque LoginGate-loop. Surface the real error so the
        // user can see what failed instead of a blank/loop screen.

        console.error("[auth] keycloak init failed:", err);
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ status, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/**
 * ROUND 13 G9 -- the auth status where a DATA hook is asking.
 *
 * Two reference reads were firing before a token existed, 401ing and
 * writing console errors under a splash that was working correctly.
 * Gating them on the auth status is the fix, and the status lives in
 * this context -- but `useAuth` THROWS without a provider, which would
 * make a data hook unusable anywhere the provider is absent and turn
 * one console error into twenty broken tests.
 *
 * So: null where there is no provider. A hook gating on this treats
 * null as "there is no auth initialisation to wait for" and proceeds,
 * because the gate exists to avoid racing a REAL initialisation. A
 * missing provider in the app itself is a structural error the root
 * layout cannot have, not a runtime state to defend against.
 */
export function useAuthStatusIfAvailable(): AuthStatus | null {
  return useContext(AuthContext)?.status ?? null;
}
